import React, {FC} from 'react'
import {Link} from 'react-router-dom'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {PlatformDataType} from 'src/interfaces/Platform.interface'
import {USER_DATA} from 'src/app/_interfaces/auth.interface'
import {AccountSetupProgress, getUserInitials} from '../utils/accountSetupProgress'
import {getHeroDisplayChannels} from '../utils/platformChannels'

type Props = {
  user: USER_DATA | null | undefined
  connectedPlatforms: PlatformDataType[]
  accountProgress: AccountSetupProgress
}

const CIRCUMFERENCE = 2 * Math.PI * 18

const DashboardHero: FC<Props> = ({user, connectedPlatforms, accountProgress}) => {
  const setupProgress = accountProgress
  const organizationName = user?.company_name?.trim()
  const displayChannels = getHeroDisplayChannels(connectedPlatforms)
  const channelsPerRow = Math.ceil(displayChannels.length / 2)
  const channelRows = [
    displayChannels.slice(0, channelsPerRow),
    displayChannels.slice(channelsPerRow),
  ].filter((row) => row.length > 0)

  const strokeOffset = CIRCUMFERENCE - (setupProgress.percent / 100) * CIRCUMFERENCE
  const isSetupHidden = !setupProgress.isLoading && setupProgress.percent >= 100

  return (
    <div className={`dashboard-hero ${isSetupHidden ? 'has-no-setup' : ''}`}>
      <div className='dashboard-hero-account d-flex align-items-center gap-3'>
        <div className='dashboard-avatar'>{getUserInitials(user?.name, user?.email)}</div>
        <div className='dashboard-welcome'>
          <h1>Hello, {user?.name || user?.email || 'there'}</h1>
          {organizationName ? (
            <p>
              Organization: <strong>{organizationName}</strong>
            </p>
          ) : null}
        </div>
      </div>

      {displayChannels.length > 0 ? (
        <div className='dashboard-hero-channels'>
          <div className='dashboard-channel-hub'>
            <span className='dashboard-channel-title'>CONNECTED INTEGRATIONS</span>
            <div className='dashboard-channel-rows'>
              {channelRows.map((row, rowIndex) => (
                <div key={`channel-row-${rowIndex}`} className='dashboard-channel-row'>
                  {row.map((channel) => (
                    <div
                      key={channel.key}
                      className='dashboard-channel-pill'
                      style={{opacity: channel.isConnected ? 1 : 0.6}}
                    >
                      <span
                        className={`dashboard-status-dot ${
                          channel.isConnected ? 'active' : 'inactive'
                        }`}
                      />
                      {channel.label}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className='dashboard-hero-channels' />
      )}

      {!setupProgress.isLoading && setupProgress.percent < 100 && (
        <OverlayTrigger
          placement='bottom'
          overlay={
            <Tooltip id='account-setup-tooltip'>
              <div className='text-start p-1' style={{fontSize: '12px'}}>
                <div className='fw-bold mb-2 border-bottom pb-1'>Account Setup</div>
                <div className='d-flex align-items-center mb-1'>
                  <span className='me-2'>{setupProgress.fields?.full_name ? '✅' : '❌'}</span> Full Name
                </div>
                <div className='d-flex align-items-center mb-1'>
                  <span className='me-2'>{setupProgress.fields?.email ? '✅' : '❌'}</span> Email
                </div>
                <div className='d-flex align-items-center mb-1'>
                  <span className='me-2'>{setupProgress.fields?.phone_number ? '✅' : '❌'}</span> Phone Number
                </div>
                <div className='d-flex align-items-center mb-1'>
                  <span className='me-2'>{setupProgress.fields?.company_name ? '✅' : '❌'}</span> Company Name
                </div>
                <div className='d-flex align-items-center'>
                  <span className='me-2'>{setupProgress.fields?.country ? '✅' : '❌'}</span> Country
                </div>
              </div>
            </Tooltip>
          }
        >
          <Link
            to='/account/settings'
            className='dashboard-account-box dashboard-hero-setup text-decoration-none'
          >
            <div className='dashboard-progress-ring'>
              <svg viewBox='0 0 44 44'>
                <circle className='dashboard-progress-bg' cx='22' cy='22' r='18' />
                <circle
                  className='dashboard-progress-bar'
                  cx='22'
                  cy='22'
                  r='18'
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={strokeOffset}
                />
              </svg>
              <div className='dashboard-progress-text'>{setupProgress.percent}%</div>
            </div>
            <div>
              <div className='dashboard-account-setup-title'>Account Setup</div>
              <div className='dashboard-account-setup-subtitle'>
                {setupProgress.remainingCount} milestone
                {setupProgress.remainingCount === 1 ? '' : 's'} remaining
              </div>
            </div>
          </Link>
        </OverlayTrigger>
      )}
    </div>
  )
}

export default DashboardHero
